import { expose } from 'comlink';
import { OPERATION } from '../../des.dto';
import { SyncedWarehouse, Warehouse } from '../../warehouse/warehouse.dto';
import { WarehouseSyncStepTaskContext } from '../executors';
import { AbstractWarehouseSyncStepWorker } from './abstract-warehouse-worker.utils';
import { ALL_WAREHOUSE_STORES, OFFLINE_WAREHOUSES_STORE, WAREHOUSES_STORE, logger } from '@damofire/data-commons';

export class WarehousesCleanupStepsWorker extends AbstractWarehouseSyncStepWorker {
  constructor() {
    super(WAREHOUSES_STORE, 'WAREHOUSES');
  }

  protected async clean(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: Warehouse; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    const warehouses: SyncedWarehouse[] = await this.idbService.getAll(WAREHOUSES_STORE);
    const maxCnt = this.environment.appConfig.watson.maxSyncingWarehouses;

    logger.info('Found warehouses in database', { count: warehouses.length, maxCount: maxCnt, category: 'sync' });
    if (warehouses.length > maxCnt && !this.shouldStopTask) {
      const offlineWarehouses: Warehouse[] = await this.idbService.getAll(OFFLINE_WAREHOUSES_STORE);
      const allWithoutOfflineWarehouses = warehouses.filter((w) => !offlineWarehouses.find((v) => v._id === w._id));
      allWithoutOfflineWarehouses.sort((a, b) => (a.lastAccessedDate < b.lastAccessedDate ? -1 : 1));

      const toDelete = allWithoutOfflineWarehouses.splice(0, allWithoutOfflineWarehouses.length - maxCnt);

      if (toDelete.length) {
        const keys = toDelete.map((d) => d._id);
        logger.info('Found warehouses to delete', { count: toDelete.length, keys, category: 'sync' });
        for (const d of toDelete) {
          try {
            await this.deleteAllFromWarehouse(d._id);
            itemsProcessedCallback?.({ data: d, operation: OPERATION.DELETE }, null);
          } catch (err) {
            logger.error('Failed to delete warehouse from IDB', { warehouseId: d._id, error: err, category: 'sync' });
          }
        }
      }
    } else {
      logger.debug('Maximum warehouse not reached, nothing to cleanup from database', { category: 'sync' });
    }
  }

  private async deleteAllFromWarehouse(warehouse: string): Promise<void> {
    logger.debug('Deleting all data from database', { warehouse, category: 'sync' });
    await this.idbService.deleteAllFromIndex(ALL_WAREHOUSE_STORES, 'division', warehouse);

    logger.debug('Deleting warehouse entry in database', { warehouse, category: 'sync' });
    await this.idbService.delete(WAREHOUSES_STORE, warehouse);
  }
}

expose(WarehousesCleanupStepsWorker);
